import { Plus, Trash2, GripVertical } from "lucide-react";
import { EVENT_REASONS_MAX_LENGTH, YARDI_ROW_ID_KEY } from "../configTabHelpers";
import companyFormStyles from "@/features/company/styles/companyAdd.module.css";
import { ConfigSubsection } from "./ConfigSubsection";
import type { YardiEventConfigTableProps } from "../../../types";

export function YardiEventConfigTable(props: YardiEventConfigTableProps) {
    const {
        addYardiRow,
        yardiEvents,
        setYardiEvents,
        availableYardiEvents,
        availableYardiActions,
        yardiDuplicateRowIndexes,
        lrSaveAttempted,
        yardiRowReorderErrors,
        setYardiDragOverIndex,
        yardiDraggingIndex,
        yardiDragOverIndex,
        handleYardiRowDrop,
        handleYardiRowDragStart,
        handleYardiRowDragEnd,
        getEventCriteriaRequired,
        getActionCommentRequired,
        yardiCommentInteracted,
        setYardiCommentInteracted,
        visibleYardiDenyGuestTooltips,
        setVisibleYardiDenyGuestTooltips,
        closeYardiDenyGuestTooltip,
        removeYardiRow,
    } = props;
    return (
        <ConfigSubsection
            title="Yardi Event Config"
            testId="company-config-yardi-event-config-section"
            headerActions={
                <button
                    type="button"
                    data-testid="company-config-yardi-event-add-row-button"
                    onClick={addYardiRow}
                    className={`${companyFormStyles.btnPrimary} gap-2 py-1.5 text-[13px]`}
                >
                    <Plus className="h-4 w-4" /> Add New Event
                </button>
            }
        >
    <div className="w-full min-w-0 overflow-x-auto">
        <table className="w-full min-w-[920px] table-fixed border-collapse">
            <colgroup>
                <col className="w-[18%]" />
                <col className="w-[18%]" />
                <col className="w-[18%]" />
                <col className="w-[34%]" />
                <col className="w-[12%]" />
            </colgroup>
            <thead>
                <tr className="bg-[#fcfcfc] border-b border-[#eaeaea]">
                    <th className="px-3 py-3 text-left text-[13px] font-semibold uppercase tracking-tight text-slate-600 sm:px-4">
                        Look for
                    </th>
                    <th className="px-3 py-3 text-left text-[13px] font-semibold uppercase tracking-tight text-slate-600 sm:px-4">
                        Event Criteria
                    </th>
                    <th className="px-3 py-3 text-left text-[13px] font-semibold uppercase tracking-tight text-slate-600 sm:px-4">
                        Take Action
                    </th>
                    <th className="min-w-0 px-3 py-3 text-left text-[13px] font-semibold uppercase tracking-tight text-slate-600 sm:px-4">
                        Comment
                    </th>
                    <th className="px-2 py-3 text-left text-[13px] font-semibold uppercase tracking-tight text-slate-600 whitespace-nowrap sm:px-3">
                        Action
                    </th>
                </tr>
            </thead>
            <tbody>
                {yardiEvents.map((row, idx) => (
                    <tr
                        key={String(row[YARDI_ROW_ID_KEY] ?? `yardi-${idx}`)}
                        data-testid={`company-config-yardi-event-row-${idx}`}
                        onDragEnter={(e) => {
                            e.preventDefault();
                        }}
                        onDragOverCapture={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                            setYardiDragOverIndex((prev) => (prev === idx ? prev : idx));
                        }}
                        onDrop={(e) => handleYardiRowDrop(e, idx)}
                        className={`border-b border-[#f0f0f0] transition-colors ${
                            yardiDraggingIndex === idx ? "opacity-60" : ""
                        } ${
                            yardiDragOverIndex === idx && yardiDraggingIndex !== idx
                                ? "bg-sky-50/90 ring-1 ring-inset ring-[#1597ff]/35"
                                : "hover:bg-slate-50/50"
                        }`}
                    >
                        <td className="px-3 py-3 align-top sm:px-4">
                            <select
                                value={row.event_type}
                                onChange={(e) => {
                                    const news = [...yardiEvents];
                                    news[idx].event_type = e.target.value;
                                    setYardiEvents(news);
                                }}
                                className={`h-10 w-full min-w-0 rounded border bg-white px-2.5 py-2 text-[13px] outline-none focus:border-[#1597ff] ${
                                    yardiDuplicateRowIndexes.has(idx) ||
                                    (lrSaveAttempted && !String(row.event_type ?? "").trim())
                                        ? "border-red-500 focus:border-red-500"
                                        : "border-[#eaeaea]"
                                }`}
                            >
                                <option value="">Select Event</option>
                                {availableYardiEvents.map((e) => (
                                    <option key={e.value} value={e.value}>{e.value}</option>
                                ))}
                            </select>
                            {yardiDuplicateRowIndexes.has(idx) && (
                                <div className="mt-1.5 flex items-start gap-2 text-[13px] text-red-600">
                                    <span
                                        className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                                        aria-hidden
                                    >
                                        !
                                    </span>
                                    <span>Event/Action combination is duplicate.</span>
                                </div>
                            )}
                            {lrSaveAttempted && !String(row.event_type ?? "").trim() && (
                                <div className="mt-1.5 flex items-start gap-2 text-[13px] text-red-600">
                                    <span
                                        className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                                        aria-hidden
                                    >
                                        !
                                    </span>
                                    <span>Event is required.</span>
                                </div>
                            )}
                        </td>
                        <td className="px-3 py-3 align-top sm:px-4">
                            {(() => {
                                const criteriaRequired = getEventCriteriaRequired(row.event_type);
                                const criteriaShowErr =
                                    lrSaveAttempted &&
                                    criteriaRequired &&
                                    !String(row.event_criteria ?? "").trim();
                                return (
                                    <>
                                        <textarea
                                            data-testid={`company-config-yardi-event-row-${idx}-event-criteria-textarea`}
                                            value={row.event_criteria}
                                            onChange={(e) => {
                                                const news = [...yardiEvents];
                                                news[idx].event_criteria = e.target.value;
                                                setYardiEvents(news);
                                            }}
                                            rows={1}
                                            disabled={!criteriaRequired}
                                            className={`h-10 w-full min-h-10 min-w-0 resize-y rounded bg-white px-2.5 py-2 text-[13px] leading-normal outline-none disabled:bg-slate-50 disabled:text-slate-400 ${
                                                criteriaShowErr
                                                    ? "border border-red-500 focus:border-red-500"
                                                    : "border border-[#eaeaea] focus:border-[#1597ff]"
                                            }`}
                                            placeholder="Event Criteria"
                                        />
                                        {criteriaShowErr && (
                                            <div className="mt-1.5 flex items-start gap-2 text-[13px] text-red-600">
                                                <span
                                                    className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                                                    aria-hidden
                                                >
                                                    !
                                                </span>
                                                <span>Event criteria is required.</span>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </td>
                        <td className="px-3 py-3 align-top sm:px-4">
                            <select
                                data-testid={`company-config-yardi-event-row-${idx}-take-action-select`}
                                value={row.lr_action}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    const news = [...yardiEvents];
                                    news[idx].lr_action = v;
                                    news[idx].event_reasons = "";
                                    setYardiEvents(news);
                                    const commentReq = getActionCommentRequired(v, availableYardiActions);
                                    setYardiCommentInteracted((prev) => {
                                        const next = [...prev];
                                        while (next.length <= idx) next.push(false);
                                        next[idx] = commentReq;
                                        return next;
                                    });
                                    setVisibleYardiDenyGuestTooltips((prev) => {
                                        const next = [...prev];
                                        while (next.length <= idx) next.push(false);
                                        next[idx] = v === "denyGuestCard" && commentReq;
                                        return next;
                                    });
                                }}
                                className={`h-10 w-full min-w-0 rounded border bg-white px-2.5 py-2 text-[13px] outline-none focus:border-[#1597ff] ${
                                    yardiDuplicateRowIndexes.has(idx) ||
                                    (lrSaveAttempted && !String(row.lr_action ?? "").trim())
                                        ? "border-red-500 focus:border-red-500"
                                        : "border-[#eaeaea]"
                                }`}
                            >
                                <option value="">Select Action</option>
                                {availableYardiActions.map((a) => (
                                    <option key={a.action_type} value={a.action_type}>{a.action_label}</option>
                                ))}
                            </select>
                            {yardiDuplicateRowIndexes.has(idx) && (
                                <div className="mt-1.5 flex items-start gap-2 text-[13px] text-red-600">
                                    <span
                                        className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                                        aria-hidden
                                    >
                                        !
                                    </span>
                                    <span>Event/Action combination is duplicate.</span>
                                </div>
                            )}
                            {yardiRowReorderErrors[idx] && (
                                <div className="mt-1.5 flex items-start gap-2 text-[13px] text-red-600">
                                    <span
                                        className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                                        aria-hidden
                                    >
                                        !
                                    </span>
                                    <span>{yardiRowReorderErrors[idx]}</span>
                                </div>
                            )}
                            {lrSaveAttempted && !String(row.lr_action ?? "").trim() && (
                                <div className="mt-1.5 flex items-start gap-2 text-[13px] text-red-600">
                                    <span
                                        className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                                        aria-hidden
                                    >
                                        !
                                    </span>
                                    <span>Action is required.</span>
                                </div>
                            )}
                        </td>
                        <td className="min-w-0 overflow-visible px-3 py-3 align-top sm:px-4">
                            {(() => {
                                const yardiCommentReq = getActionCommentRequired(
                                    row.lr_action,
                                    availableYardiActions,
                                );
                                const yardiCommentEmpty = !String(row.event_reasons ?? "").trim();
                                const yardiReasonLen = String(row.event_reasons ?? "").length;
                                const yardiOverMax = yardiReasonLen > EVENT_REASONS_MAX_LENGTH;
                                const yardiAtMax =
                                    yardiCommentReq &&
                                    yardiReasonLen === EVENT_REASONS_MAX_LENGTH;
                                const yardiShowRequiredError =
                                    yardiCommentReq &&
                                    yardiCommentEmpty &&
                                    (yardiCommentInteracted[idx] || lrSaveAttempted);
                                return (
                                    <div className="min-w-0">
                                        <div className="relative min-w-0">
                                            <textarea
                                                data-testid={`company-config-yardi-event-row-${idx}-comment-textarea`}
                                                value={row.event_reasons}
                                                onChange={(e) => {
                                                    const news = [...yardiEvents];
                                                    news[idx].event_reasons = e.target.value;
                                                    setYardiEvents(news);
                                                    setYardiCommentInteracted((prev) => {
                                                        const next = [...prev];
                                                        while (next.length <= idx) next.push(false);
                                                        next[idx] = true;
                                                        return next;
                                                    });
                                                }}
                                                onBlur={() =>
                                                    setYardiCommentInteracted((prev) => {
                                                        const next = [...prev];
                                                        while (next.length <= idx) next.push(false);
                                                        next[idx] = true;
                                                        return next;
                                                    })
                                                }
                                                rows={1}
                                                disabled={!yardiCommentReq}
                                                className={`h-10 min-h-10 w-full min-w-0 resize-y rounded border bg-white px-2.5 py-2 text-[13px] leading-normal text-slate-800 outline-none focus:outline-none disabled:bg-slate-50 disabled:text-slate-400 ${
                                                    yardiShowRequiredError || yardiOverMax
                                                        ? "border-red-500 focus:border-red-500"
                                                        : "border-[#eaeaea] focus:border-[#1597ff]"
                                                }`}
                                                placeholder="Please enter action comment"
                                                maxLength={EVENT_REASONS_MAX_LENGTH}
                                            />
                                            {row.lr_action === "denyGuestCard" &&
                                                visibleYardiDenyGuestTooltips[idx] && (
                                                    <div className="event_reasons_info_yardi relative z-[150] mt-2 w-full max-w-[min(100%,28rem)] rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-left text-[13px] leading-snug text-amber-900 shadow-lg">
                                                        <button
                                                            type="button"
                                                            data-testid={`company-config-yardi-event-row-${idx}-deny-guest-tooltip-close-button`}
                                                            className="absolute right-2 top-2 border-0 bg-transparent p-0 text-base leading-none text-amber-800 hover:text-amber-950"
                                                            onClick={() => closeYardiDenyGuestTooltip(idx)}
                                                            aria-label="Close"
                                                        >
                                                            ×
                                                        </button>
                                                        <div className="pr-7">
                                                            Please provide a valid reason that matches an
                                                            entry on your Yardi portal. Failure to do so may
                                                            result in this action being rejected.
                                                        </div>
                                                    </div>
                                                )}
                                        </div>
                                        {yardiShowRequiredError && (
                                            <div className="mt-1.5 flex items-start gap-2 text-[13px] font-normal text-red-600">
                                                <span
                                                    className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                                                    aria-hidden
                                                >
                                                    !
                                                </span>
                                                <span>Action comment is required.</span>
                                            </div>
                                        )}
                                        {yardiAtMax && (
                                            <p className="mt-1.5 text-[12px] leading-snug text-slate-500">
                                                Maximum {EVENT_REASONS_MAX_LENGTH} characters.
                                            </p>
                                        )}
                                        {yardiOverMax && (
                                            <div className="mt-1.5 flex items-start gap-2 text-[13px] text-red-600">
                                                <span
                                                    className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                                                    aria-hidden
                                                >
                                                    !
                                                </span>
                                                <span>Action comment must not exceed 240 characters.</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </td>
                        <td className="px-2 py-3 align-top sm:px-3">
                            <div className="flex items-center justify-center gap-1">
                                <button
                                    type="button"
                                    data-testid={`company-config-yardi-event-row-${idx}-remove-row-button`}
                                    onClick={() => removeYardiRow(idx)}
                                    disabled={idx === 0}
                                    className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded border border-[#e5e5e5] bg-white p-0 transition-colors ${
                                        idx === 0
                                            ? "cursor-not-allowed text-slate-300"
                                            : "text-slate-400 hover:border-slate-300 hover:text-red-500"
                                    }`}
                                    title="Remove row"
                                    aria-label="Remove row"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                                <span
                                    data-testid={`company-config-yardi-event-row-${idx}-drag-handle`}
                                    draggable={idx !== 0}
                                    onDragStart={(e) => {
                                        if (idx === 0) {
                                            e.preventDefault();
                                            return;
                                        }
                                        e.stopPropagation();
                                        handleYardiRowDragStart(e, idx);
                                    }}
                                    onDragEnd={handleYardiRowDragEnd}
                                    className={`inline-flex h-9 w-9 shrink-0 select-none items-center justify-center rounded border border-[#e5e5e5] bg-white touch-none ${
                                        idx === 0
                                            ? "cursor-not-allowed text-slate-300"
                                            : "cursor-grab text-slate-400 hover:border-slate-300 hover:text-slate-600 active:cursor-grabbing"
                                    }`}
                                    title={
                                        idx === 0
                                            ? "First row cannot be reordered"
                                            : "Drag to reorder"
                                    }
                                    role="button"
                                    tabIndex={idx === 0 ? -1 : 0}
                                    aria-label="Drag to reorder row"
                                    aria-disabled={idx === 0}
                                >
                                    <GripVertical className="pointer-events-none h-4 w-4" />
                                </span>
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
        </ConfigSubsection>
    );
}
